import React from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FiHome, FiPlus, FiEdit2, FiEye } from 'react-icons/fi';

export default function FacilitiesIndex({ facilities = [], user }) {
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Facilities</h1>
              <p className="text-gray-500">Manage your assisted living and memory care facilities</p>
            </div>
            <Button className="flex items-center" asChild>
              <Link href="/facility_owner/facilities/new">
                <FiPlus className="mr-2 h-4 w-4" />
                Add New Facility
              </Link>
            </Button>
          </div>

          {facilities.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {facilities.map((facility) => (
                <Card key={facility.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  {facility.main_photo_url ? (
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={facility.main_photo_url} 
                        alt={facility.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 flex items-center justify-center">
                      <FiHome className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                  
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{facility.name}</CardTitle>
                        <CardDescription>
                          {facility.organization?.name || 'No organization'}
                        </CardDescription>
                      </div>
                      <Badge variant={facility.published ? "success" : "secondary"}>
                        {facility.published ? "Published" : "Draft"}
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-sm text-gray-500">
                        {facility.units?.length || 0} units · 
                        {facility.units?.filter(u => u.available_count > 0).length || 0} available
                      </p>
                    </div>
                    
                    <div className="flex justify-between mt-4">
                      <Button variant="outline" size="sm" className="flex items-center" asChild>
                        <Link href={`/facilities/${facility.slug}`} target="_blank">
                          <FiEye className="mr-1 h-4 w-4" />
                          View
                        </Link>
                      </Button>
                      <Button size="sm" className="flex items-center" asChild>
                        <Link href={`/facility_owner/facilities/${facility.id}/edit`}>
                          <FiEdit2 className="mr-1 h-4 w-4" />
                          Edit
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <FiHome className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No facilities yet</h3>
                <p className="text-gray-500 mb-4">Get started by adding your first facility.</p>
                <Button asChild>
                  <Link href="/facility_owner/facilities/new">
                    <FiPlus className="mr-2 h-4 w-4" />
                    Add New Facility
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
